import _ from 'lodash'
import { Dictionary, isNotEmptyElement, removeArrayDuplicates } from '@feedyou/utils'
import { GlobalStorage, IntegrationAction, IntegrationService } from '@feedyou/schema'

import { Storage, StorageType } from './types'
import { slugifyStorage } from '../../lib/transliterate'
import { Option, toSelectOption } from '../../lib/options'
import { INTEGRATION_STORAGE_NAMES, STATIC_READONLY_STORAGE_NAMES } from './constants'

export function mergeStoragesByName(storages: Storage[]): Storage[] {
  const storagesByName = _.groupBy(storages, 'name')

  return Object.values(storagesByName).map(mergeStoragesWithSameName)
}

const mergeStoragesWithSameName = (storages: Storage[]): Storage => {
  const allTypes = removeArrayDuplicates(storages.flatMap(s => s?.storageType))

  return _.merge({}, ...storages, { storageType: allTypes })
}

export function containsInterpolatedStorage(storage: string): boolean {
  return storage.includes('{') || storage.includes('}')
}

export const getStorageNamesFromIntegrationStorageSchema = (
  service: IntegrationService,
  action: IntegrationAction
) => {
  return INTEGRATION_STORAGE_NAMES[service!]?.[action!] ?? []
}

export function getStorageFromOption(option: Option | null): string | undefined {
  if (!option) {
    return
  }

  const value = option.value?.trim() || ''
  const valueSlugify = slugifyStorage(value)

  if (containsInterpolatedStorage(value)) {
    return
  }

  return valueSlugify
}

export const createOptionsFromStorageList = (storages: Storage[]): Option[] => {
  return storages
    .map(storage => storage.name)
    .filter(isNotEmptyElement)
    .map(name => toSelectOption(name))
}

export function isStorageWritable(
  storageOrName: Storage | string | undefined | null,
  botVariables: Dictionary<string, any> = {},
  globalStorages: Dictionary<string, GlobalStorage> = {}
): boolean {
  if (!storageOrName) return false

  const storageName = typeof storageOrName === 'string' ? storageOrName : storageOrName.name

  if (!storageName) return false

  return (
    !storageName.includes('.') &&
    !isStaticReadonlyStorageName(storageName) &&
    !botVariables[storageName] &&
    !globalStorages[storageName]?.readonly
  )
}

export const isStaticReadonlyStorageName = (storageName: string): boolean => {
  return STATIC_READONLY_STORAGE_NAMES.includes(storageName)
}

export function getAllStorageTypes(): StorageType[] {
  return [
    'system',
    'user',
    'NLP entity',
    'webchat user data',
    'integration',
    'environment',
    'global'
  ]
}
